import { Routes, Route, useNavigate } from "react-router-dom";
import { AppShell, Container, Loader, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from "@mantine/hooks";
import { IconHome, IconTag, IconMapPin, IconUsers, IconClipboardList, IconBox, IconBuildingStore, IconClipboardCopy, IconClock } from "@tabler/icons-react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { NavigationBar, NavigationItem, useNavigationBarState } from "./Components/layout/NavigationBar";
import { HeaderBar } from "./Components/layout/HeaderBar";
import Home from './Pages/Home';
import WorkOrdersPage from "./Pages/WorkOrders/Index";
import WorkOrderTemplatesPage from "./Pages/WorkOrderTemplates/Index";
import WorkOrderSchedulesPage from "./Pages/WorkOrderSchedules/Index";
import CategoriesPage from "./Pages/Categories/Index";
import LocationsPage from "./Pages/Locations/Index";
import AssetsPage from "./Pages/Assets/Index";
import VendorsPage from "./Pages/Vendors/Index";
import UsersPage from "./Pages/Users/Users";
import TeamsPage from "./Pages/Users/Teams";
import PreferencesPage from "./Pages/Preferences/Index";
import { RoleTemplate } from "./Components/RoleTemplate";
import { NoAccess } from "./Components/Shared/NoAccess";
import { AzureADRoles } from "./Auth/auth-roles";

import './App.css';

const App = () => {
	const { instance, inProgress } = useMsal();
	const account = instance.getActiveAccount();
	const navigate = useNavigate();
	const { colorScheme } = useMantineColorScheme();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	
	const { isOpen: navigationOpened, toggle: toggleNavigation } = useNavigationBarState();

	const logoPath = colorScheme === "light" ? `/${import.meta.env.VITE_ICON192}` : `/${import.meta.env.VITE_ICON192_WHITE}`;
	const userName = account?.name;

	const handleSwitchAccount = () => {
		instance.loginRedirect({
			prompt: "select_account",
			scopes: []
		});
	}

	const handleLogout = () => {
		instance.logout({
			// account: account,
			// postLogoutRedirectUri: import.meta.env.VITE_CLIENT_DOMAIN,
			onRedirectNavigate: () => false
		});

		navigate('/');
	}

	const navigationItems: NavigationItem[] = [
		{ link: '/', label: 'Home', icon: IconHome },
		{ link: '/workorders', label: 'Work Orders', icon: IconClipboardList },
		{ link: '/categories', label: 'Categories', icon: IconTag },
		{ link: '/locations', label: 'Locations', icon: IconMapPin },
		{ divider: true, link: '', label: '', icon: () => null, roleRequired: AzureADRoles.AdminOrFullUser },
		{ link: '/workordertemplates', label: 'Templates', icon: IconClipboardCopy, roleRequired: AzureADRoles.Admin },
		{ link: '/workorder-schedules', label: 'Schedules', icon: IconClock, roleRequired: AzureADRoles.Admin },
		{ link: '/assets', label: 'Assets', icon: IconBox, roleRequired: AzureADRoles.AdminOrFullUser },
		{ link: '/vendors', label: 'Vendors', icon: IconBuildingStore, roleRequired: AzureADRoles.AdminOrFullUser },
		{ divider: true, link: '', label: '', icon: () => null, roleRequired: AzureADRoles.Admin },
		{ link: '/users', label: 'Users & Teams', icon: IconUsers, roleRequired: AzureADRoles.Admin }
	];

	// document.title = import.meta.env.VITE_TITLE;

	return <RoleTemplate role={AzureADRoles.All} noAccess={<NoAccess />}>
		<AppShell
			header={{ height: 60 }}
			navbar={{ width: navigationOpened ? 220 : 60, breakpoint: 'sm', collapsed: { mobile: !navigationOpened, desktop: false } }}
			padding="md"
			layout="alt"
			withBorder={true}
		>
			<AppShell.Header withBorder={true}>
				<HeaderBar />
			</AppShell.Header>
			<AppShell.Navbar zIndex={1}>
				<NavigationBar 
					title={import.meta.env.VITE_NAV_TITLE} 
					logoPath={logoPath} 
					items={navigationItems} 
					userName={userName} 
					onSwitchAccount={handleSwitchAccount} 
					onLogout={handleLogout} />
			</AppShell.Navbar>
			<AppShell.Main>
			<Container px={isMobile ? 0 : 'sm'} py={isMobile ? 0 : 'sm'} miw={345} maw={1200}>
				{inProgress != InteractionStatus.None && <Loader />}
				{inProgress == InteractionStatus.None && <>
				<Routes>
					<Route path="/" Component={Home} />
					<Route path="/categories" Component={CategoriesPage} />
					<Route path="/categories/:categoryId" Component={CategoriesPage} />
					<Route path="/categories/:categoryId/edit" Component={CategoriesPage} />
					<Route path="/locations" Component={LocationsPage} />
					<Route path="/locations/:locationId" Component={LocationsPage} />
					<Route path="/locations/:locationId/edit" Component={LocationsPage} />
					<Route path="/workorders" Component={WorkOrdersPage} />
					<Route path="/workorders/:workOrderId" Component={WorkOrdersPage} />
					<Route path="/workorders/:workOrderId/edit" Component={WorkOrdersPage} />
					<Route path="/workorders/:workOrderId/copy" Component={WorkOrdersPage} />
					<Route path="/workorders/:workOrderId/template/:workOrderTemplateId" Component={WorkOrdersPage} />
					<Route path="/preferences" Component={PreferencesPage} />

					<Route path="/assets/*" element={<>
						<RoleTemplate role={AzureADRoles.AdminOrFullUser} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={AssetsPage} />
								<Route path="/:assetId" Component={AssetsPage} />
								<Route path="/:assetId/edit" Component={AssetsPage} />
							</Routes>
						</RoleTemplate>
					</>} />
					<Route path="/vendors/*" element={<> 
						<RoleTemplate role={AzureADRoles.AdminOrFullUser} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={VendorsPage} />
								<Route path="/:vendorId" Component={VendorsPage} />
								<Route path="/:vendorId/edit" Component={VendorsPage} />
							</Routes>
						</RoleTemplate>
					</>} />
					<Route path="/workordertemplates/*" element={<> 
						<RoleTemplate role={AzureADRoles.Admin} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={WorkOrderTemplatesPage} />
								<Route path="/:workOrderTemplateId" Component={WorkOrderTemplatesPage} />
								<Route path="/:workOrderTemplateId/edit" Component={WorkOrderTemplatesPage} />
							</Routes>
						</RoleTemplate>
					</>} />
					<Route path="/workorder-schedules/*" element={<> 
						<RoleTemplate role={AzureADRoles.Admin} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={WorkOrderSchedulesPage} />
								<Route path="/:scheduleId" Component={WorkOrderSchedulesPage} />
								<Route path="/:scheduleId/edit" Component={WorkOrderSchedulesPage} />
							</Routes>
						</RoleTemplate>
					</>} />
					<Route path="/users/*" element={<> 
						<RoleTemplate role={AzureADRoles.Admin} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={UsersPage} />
								<Route path="/:userId" Component={UsersPage} />
								<Route path="/:userId/edit" Component={UsersPage} />
							</Routes>
						</RoleTemplate>
					</>} />
					<Route path="/teams/*" element={<> 
						<RoleTemplate role={AzureADRoles.Admin} noAccess={<NoAccess />}>
							<Routes>
								<Route path="/" Component={TeamsPage} />
								<Route path="/:teamId" Component={TeamsPage} />
								<Route path="/:teamId/edit" Component={TeamsPage} />
							</Routes>
						</RoleTemplate>
					</>} />
				</Routes>
				</>}
			</Container>
			</AppShell.Main>
		</AppShell>
	</RoleTemplate>
}

export default App